import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { Item } from "@common/ADAPT.Common.Model/organisation/item";
import { Objective } from "@common/ADAPT.Common.Model/organisation/objective";
import { WatcherLink } from "@common/ADAPT.Common.Model/organisation/watcher-link";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { PeopleQueryUtilities } from "@common/user/people-query-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { MentionPersonComponent } from "@common/ux/html-editor/mention-person/mention-person.component";
import { IAdaptMenuItem, MenuComponent } from "@common/ux/menu/menu.component";
import { ObjectivesAuthService } from "@org-common/lib/objectives/objectives-auth.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { debounceTime, filter, Subject } from "rxjs";
import { WatcherService } from "../watcher.service";

@Component({
    selector: "adapt-current-watchers-view",
    templateUrl: "./current-watchers-view.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class CurrentWatchersViewComponent extends BaseComponent implements OnInit {
    @Input() public item?: Item;
    @Input() public objective?: Objective;

    public componentChanged$ = new Subject<void>();
    public hasWatcher = false;
    public menuItems: IAdaptMenuItem[] = [];
    @ViewChild(MenuComponent) private menuComponent?: MenuComponent;

    public constructor(
        private watcherService: WatcherService,
        private teamsService: CommonTeamsService,
        private commonDataService: CommonDataService,
        private objectivesAuthService: ObjectivesAuthService,
        elementRef: ElementRef,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super(elementRef);

        rxjsBreezeService.entityTypeChanged(WatcherLink).pipe(
            filter((watcherLink) => (!!watcherLink.itemId && watcherLink.itemId === this.item?.itemId) ||
                (!!watcherLink.objectiveId && watcherLink.objectiveId === this.objective?.objectiveId)),
            debounceTime(100),
            this.takeUntilDestroyed(),
        ).subscribe(async () => {
            await this.ngOnInit();
            this.menuComponent?.refresh();
            // emit component changed event in the next digest cycle (at least wait for the changes to be rendered)
            setTimeout(() => this.componentChanged$.next());
        });
    }

    public async ngOnInit() {
        this.hasWatcher = false;
        if (this.item) {
            await this.initMenuItemsForItem(this.item);
        } else if (this.objective) {
            await this.initMenuItemsForObjective(this.objective);
        }
    }

    private async initMenuItemsForItem(item: Item) {
        const currentWatcherItems = item.watcherLinks.map((watcherLink) =>
            this.createMentionPersonMenuItem(watcherLink.person, true, () => this.watcherService.removeWatcherLink(watcherLink).subscribe()));
        this.hasWatcher = currentWatcherItems.length > 0;

        let otherTeamMemberItems: IAdaptMenuItem[] = [];
        if (item.board?.team) {
            const activeTeamMembers = await this.teamsService.promiseToGetActiveTeamMembers(item.board?.team);
            otherTeamMemberItems = activeTeamMembers
                .filter((person) => !item.watcherLinks.find((watcherLink) => watcherLink.personId === person.personId)
                    && person.personId !== item.assigneeId)
                .map((person) => this.createMentionPersonMenuItem(person, false, () => this.watcherService.addWatcherLink(person.personId, item).subscribe()));
            if (otherTeamMemberItems.length > 0 && this.hasWatcher) {
                otherTeamMemberItems[0].separatorTop = true;
            }
        }

        this.menuItems = [
            ...currentWatcherItems,
            ...otherTeamMemberItems,
        ];

        this.checkAddBottomSeparator();
    }

    private async initMenuItemsForObjective(objective: Objective) {
        const currentWatcherItems = objective.watcherLinks.map((watcherLink) =>
            this.createMentionPersonMenuItem(watcherLink.person, true, () => this.watcherService.removeWatcherLink(watcherLink).subscribe()));
        this.hasWatcher = currentWatcherItems.length > 0;

        const activePeople = await new PeopleQueryUtilities(this.commonDataService)
            .promiseToGetActivePeople();
        const otherWatcherItems = activePeople
            // currentWatcherItems will have all people who has 'personCanEditObjectivesForTeam' permission (just like team member in action watcher menu)
            // - if there is no team (org objective), person need to be able to edit org objectives
            .filter((person) => this.objectivesAuthService.personCanEditObjectivesForTeam(person, objective))
            .filter((person) => !objective.watcherLinks.find((watcherLink) => watcherLink.personId === person.personId) &&
                person.personId !== objective.assigneePersonId)
            .map((person) => this.createMentionPersonMenuItem(person, false,
                () => this.watcherService.addWatcherLink(person.personId, undefined, objective).subscribe()));
        if (otherWatcherItems.length > 0 && this.hasWatcher) {
            otherWatcherItems[0].separatorTop = true;
        }

        this.menuItems = [
            ...currentWatcherItems,
            ...otherWatcherItems,
        ];

        this.checkAddBottomSeparator();
    }

    private checkAddBottomSeparator() {
        if (this.menuItems.length > 0) {
            // add a separator at the bottom of the menu
            this.menuItems[this.menuItems.length - 1].separatorBottom = true;
        }
    }

    private createMentionPersonMenuItem(person: Person, isChecked: boolean, onClick: () => void) {
        return {
            templateType: MentionPersonComponent,
            templateInputKey: "person",
            templateData: person,
            templateId: String(person.personId),
            isChecked,
            onClick: () => {
                onClick();
                // this is causing onItemClicked to not called - causing the checkbox to not update
                // e.event?.stopPropagation();
            },
        } as IAdaptMenuItem;
    }
}
